import React, { useMemo, useCallback } from 'react';
import { Text } from '../Text';
import { FieldRequirementTypes } from '@tradingblock/types';
import { getFieldRequirements, isRequiredLengthValid, isRequiredDigitsValid, isRequiredLowercaseValid, isRequiredUppercaseValid, isRequiredSpecialCharsValid, getSpecialCharsAsHtml, isUserNameContainedInPassword, isUserNameEqualToEmail, doesPasswordContainForbiddenKeywords, } from '../../../utilities';
import { FieldRequirement } from './FieldRequirement';
export const FieldRequirementsHelp = ({ values, field, formSubmitted, userRequirementsInfo, context, requirementOverrides }) => {
    const value = values && values[field] || '';
    const requirements = getFieldRequirements(field, userRequirementsInfo);
    const charData = useMemo(() => {
        return {
            chars: getSpecialCharsAsHtml(requirements.acceptableSpecialChars),
        };
    }, [requirements]);
    const hintValidation = (validationFunction) => {
        if (context === 'createAccount') {
            // TODO: Determine undefined condition.
            return validationFunction ? 'var(--teal)' : 'var(--red)';
        }
        if (context === 'resetPassword') {
            if (validationFunction === undefined) {
                return 'white';
            }
            return validationFunction ? 'var(--blue-dark)' : 'var(--red)';
        }
    };
    const getFieldRequirementProps = useCallback((type) => {
        switch (type) {
            case 'length-min':
            case 'length-range':
                return {
                    type,
                    min: requirements.minLength,
                    max: requirements.maxLength,
                    valid: formSubmitted ? isRequiredLengthValid(value, requirements.minLength, requirements.maxLength) : undefined,
                    color: hintValidation(isRequiredLengthValid(value, requirements.minLength, requirements.maxLength))
                };
            case 'lower-min':
                return {
                    type,
                    min: requirements.minLowerCount,
                    valid: formSubmitted ? isRequiredLowercaseValid(value, requirements.minLowerCount) : undefined,
                    color: hintValidation(isRequiredLowercaseValid(value, requirements.minLowerCount))
                };
            case 'upper-min':
                return {
                    type,
                    min: requirements.minUpperCount,
                    valid: formSubmitted ? isRequiredUppercaseValid(value, requirements.minUpperCount) : undefined,
                    color: hintValidation(isRequiredUppercaseValid(value, requirements.minUpperCount))
                };
            case 'digit-min':
                return {
                    type,
                    min: requirements.minDigitCount,
                    valid: formSubmitted ? isRequiredDigitsValid(value, requirements.minDigitCount) : undefined,
                    color: hintValidation(isRequiredDigitsValid(value, requirements.minDigitCount))
                };
            case 'special-min':
                return {
                    type,
                    min: requirements.minSpecialCount,
                    valid: formSubmitted ? isRequiredSpecialCharsValid(value, requirements.minSpecialCount, requirements.acceptableSpecialChars) : undefined,
                    color: hintValidation(isRequiredSpecialCharsValid(value, requirements.minSpecialCount, requirements.acceptableSpecialChars)),
                    data: charData,
                };
            case 'special-acceptable':
                return {
                    type,
                    min: -1,
                    valid: formSubmitted ? isRequiredSpecialCharsValid(value, 0, requirements.acceptableSpecialChars) : undefined,
                    color: hintValidation(isRequiredSpecialCharsValid(value, 0, requirements.acceptableSpecialChars)),
                    data: charData,
                };
            case 'contains-username':
                if (context === 'resetPassword') {
                    return {
                        type,
                        min: -1,
                        valid: formSubmitted ? requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet : undefined,
                        color: hintValidation(requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet),
                    };
                }
                return {
                    type,
                    min: -1,
                    valid: formSubmitted && field === 'password' ? !isUserNameContainedInPassword(values.password, values.username) : undefined,
                    color: hintValidation(field === 'password' && !isUserNameContainedInPassword(values.password, values.username)),
                };
            case 'equals-email':
                return {
                    type,
                    min: -1,
                    valid: formSubmitted && field === 'username' ? !isUserNameEqualToEmail(values.username, values.email) : undefined,
                    color: hintValidation(field === 'username' && !isUserNameEqualToEmail(values.username, values.email)),
                };
            case 'contains-first-name':
                if (context === 'resetPassword') {
                    return {
                        type,
                        min: -1,
                        valid: formSubmitted ? requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet : undefined,
                        color: hintValidation(requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet),
                    };
                }
            // TODO: Add case for create account context.
            case 'contains-last-name':
                if (context === 'resetPassword') {
                    return {
                        type,
                        min: -1,
                        valid: formSubmitted ? requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet : undefined,
                        color: hintValidation(requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet),
                    };
                }
            // TODO: Add case for create account context.
            case 'contains-forbidden-keywords':
                if (context === 'resetPassword') {
                    return {
                        type,
                        min: -1,
                        valid: formSubmitted ? !doesPasswordContainForbiddenKeywords(value) : undefined,
                        color: hintValidation(!doesPasswordContainForbiddenKeywords(value)),
                    };
                }
            // TODO: Add case for create account context.
            case 'repeated-password':
                if (context === 'resetPassword') {
                    return {
                        type,
                        min: -1,
                        valid: formSubmitted ? requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet : undefined,
                        color: hintValidation(requirementOverrides && requirementOverrides[type] && requirementOverrides[type].requirementMet),
                    };
                }
            // TODO: Add case for create account context.
        }
    }, [requirements, field, value, values, formSubmitted, charData]);
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { className: `mb-1 ${context === 'resetPassword' ? 'password-requirements-header' : ''}` },
            React.createElement(Text, { id: `${field}.help`, page: "secure" })),
        React.createElement("ul", null,
            field === 'password' && (React.createElement("div", { className: "d-flex" },
                React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.ContainsUsername))),
                context === 'resetPassword' && React.createElement("div", { className: "ml-1 asterisk" }, "*"))),
            field === 'password' && (React.createElement("div", { className: "d-flex" },
                React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.ContainsFirstName))),
                context === 'resetPassword' && React.createElement("div", { className: "ml-1 asterisk" }, "*"))),
            field === 'password' && (React.createElement("div", { className: "d-flex" },
                React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.ContainsLastName))),
                context === 'resetPassword' && React.createElement("div", { className: "ml-1 asterisk" }, "*"))),
            field === 'password' && (React.createElement("div", { className: "d-flex" },
                React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.RepeatedPassword))),
                context === 'resetPassword' && React.createElement("div", { className: "ml-1 asterisk" }, "*"))),
            context === 'resetPassword' && React.createElement("p", { className: "validation-timing-note mt-1 mb-1" }, "* These checks will be performed once you click submit."),
            React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(requirements.maxLength ? FieldRequirementTypes.LengthRange : FieldRequirementTypes.LengthMin))),
            React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.LowerMin))),
            React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.UpperMin))),
            React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.DigitMin))),
            React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.SpecialMin))),
            !requirements.minSpecialCount && requirements.acceptableSpecialChars && React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.SpecialAcceptable))),
            field === 'username' && context === 'createAccount' && React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.EqualsEmail))),
            field === 'password' && React.createElement(FieldRequirement, Object.assign({}, getFieldRequirementProps(FieldRequirementTypes.ContainsForbiddenKeywords))))));
};
//# sourceMappingURL=FieldRequirementsHelp.js.map